import C from "@/modules/dashboard/Dashboard.module.css";

import { BadgeWithTip } from "@/components/Badge.tsx";
import { WTabs, type WTabItem } from "@/components/WTabs.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { AcceptInvitationModal } from "@/modules/access/AcceptInvitationModal.tsx";
import {
    accessDelegationListQuery,
    type AccessDelegationWithType,
} from "@/modules/access/delegation/accessDelegationApi.ts";
import {
    accessImpersonationListQuery,
    type AccessImpersonationWithType,
} from "@/modules/access/impersonation/accessImpersonationApi.ts";
import { ResourceCreateLinks } from "@/modules/api/resourceCreateLinks.ts";
import { useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { isMobileDevice } from "@/modules/main/mobile/isMobile.ts";
import { ActAsModal } from "@/modules/user/ActAsModal.tsx";
import { NameAvatar } from "@/modules/user/UserAvatar.tsx";
import { profileLink } from "@/modules/user/profileLink.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { AccessDelegationImpersonation } from "@warrenio/api-spec/spec.oats.gen";
import { Suspense, type ReactElement } from "react";
import { GridList, GridListItem } from "react-aria-components";
import { sortBy } from "remeda";

interface UserAccessProps {
    impersonations: ReactElement[];
    delegations: ReactElement[];
    isLoading?: boolean;
}

function ImpersonationItem({ item }: { item: AccessImpersonationWithType }) {
    return (
        <GridListItem className={C.ListItem} textValue={item.access_owner}>
            <NameAvatar name={item.access_owner} />

            <div className={cn(C.Content, "font-size-small")}>{item.access_owner}</div>

            <div className={isMobileDevice ? "" : C.Hover}>
                {item.is_accepted ? (
                    <ActAsModal item={item} />
                ) : (
                    <AcceptInvitationModal uuid={item.uuid} name={item.access_owner} />
                )}
            </div>
        </GridListItem>
    );
}

function DelegationItem({ item }: { item: AccessDelegationImpersonation }) {
    const { t } = useLingui();

    return (
        <GridListItem className={C.ListItem} textValue={item.grantee_username}>
            <NameAvatar name={item.grantee_username} />

            <div className={cn(C.Content, "font-size-small")}>{item.grantee_username}</div>

            {!item.is_accepted && (
                <div className={isMobileDevice ? "" : C.Hover}>
                    <BadgeWithTip
                        description={t`User must accept access invitation from their profile view`}
                        color="warning"
                    >
                        <Trans>Pending</Trans>
                    </BadgeWithTip>
                </div>
            )}
        </GridListItem>
    );
}

function UserAccessCustom({ impersonations, delegations, isLoading }: UserAccessProps) {
    const titleOne = <Trans>I can access</Trans>;
    const titleTwo = <Trans>Can access me</Trans>;

    const tabs: WTabItem[] = [
        { id: "one", title: titleOne, content: <ICanAccess isLoading={isLoading} items={impersonations} /> },
        { id: "two", title: titleTwo, content: <CanAccessMe isLoading={isLoading} items={delegations} /> },
    ];

    return (
        <div className={C.Access}>
            <div className={cn(C.BlockTitle, "pl-2")}>
                <h2>
                    <Trans>User access</Trans>
                </h2>

                <WButton
                    className={C.Button}
                    color="primary"
                    variant="ghost"
                    icon="jp-icon-add"
                    action={ResourceCreateLinks.access_delegation}
                >
                    <Trans>Share access</Trans>
                </WButton>
            </div>

            <div className={C.Block}>
                <WTabs compact items={tabs} />
            </div>
        </div>
    );
}

interface UserAccessLoaderProps extends Omit<UserAccessProps, "impersonations" | "delegations"> {}

function UserAccessLoader({ ...props }: UserAccessLoaderProps) {
    const { data: impersonations } = useStandardSuspenseQuery(accessImpersonationListQuery);
    const impersonationItems = sortBy([...impersonations.values()], (a) => a.created_at);
    const { data: delegations } = useStandardSuspenseQuery(accessDelegationListQuery);
    const delegationsItems = sortBy(
        [...delegations.values()].flatMap((item: AccessDelegationWithType) => item.impersonations),
        (a) => a.created_at,
    );

    return (
        <UserAccessCustom
            impersonations={impersonationItems.map((item: AccessImpersonationWithType, index: number) => (
                <ImpersonationItem key={index} item={item} />
            ))}
            delegations={delegationsItems.map((item: AccessDelegationImpersonation, index: number) => (
                <DelegationItem key={index} item={item} />
            ))}
            {...props}
        />
    );
}

export function UserAccess(props: UserAccessLoaderProps) {
    const instantLoading = <Loading key="loading" delay={0} icon="none" />;

    return (
        <Suspense
            fallback={
                <UserAccessCustom
                    {...props}
                    isLoading={true}
                    impersonations={[instantLoading]}
                    delegations={[instantLoading]}
                />
            }
        >
            <UserAccessLoader {...props} />
        </Suspense>
    );
}

interface ICanAccessProps {
    items: ReactElement[];
    isLoading?: boolean;
}

function ICanAccess({ items, isLoading }: ICanAccessProps) {
    return items?.length ? (
        isLoading ? (
            <div className={C.Loading}>{items}</div>
        ) : (
            <>
                <p className="color-muted pt-4 font-size-small">
                    <Trans>Users I can access</Trans>
                </p>

                <GridList className={C.Scrollable} aria-label="Users I can access" selectionMode="single">
                    {items}
                </GridList>

                <div className="text-center pt-4">
                    <ManageProfileButton />
                </div>
            </>
        )
    ) : (
        <div className={cn(C.Empty, "color-muted")}>
            <div>
                <MaskIcon className="jp-user-group-icon size-4rem" />
                <p className="pt-2">
                    <Trans>No one has shared access with you yet</Trans>
                </p>
            </div>
        </div>
    );
}

function CanAccessMe({ items, isLoading }: ICanAccessProps) {
    return items?.length ? (
        isLoading ? (
            <div className={C.Loading}>{items}</div>
        ) : (
            <>
                <p className="color-muted pt-4 font-size-small">
                    <Trans>Users who can access my account</Trans>
                </p>

                <GridList className={C.Scrollable} aria-label="Users who can access my account" selectionMode="single">
                    {items}
                </GridList>

                <div className="text-center pt-4">
                    <ManageProfileButton />
                </div>
            </>
        )
    ) : (
        <div className={cn(C.Empty, "color-muted")}>
            <div>
                <MaskIcon className="jp-user-group-icon size-4rem" />
                <p className="pt-2 pb-2">
                    <Trans>No one can access your user account yet</Trans>
                </p>
                <WButton
                    icon="jp-icon-add"
                    variant="ghost"
                    color="primary"
                    action={ResourceCreateLinks.access_delegation}
                >
                    <Trans comment="Button action">Share access</Trans>
                </WButton>
            </div>
        </div>
    );
}

const ManageProfileButton = () => (
    <WButton color="primary" variant="ghost" icon="jp-user-group-icon" action={profileLink}>
        <Trans>Manage</Trans>
    </WButton>
);
